import { useMemo } from 'react';

import { ChakraBox, ChakraFlex, EquipUITheme } from '@equip.health/ui';

import {
  NEW_MENTIONS,
  NEW_MESSAGES,
  NEW_REPLIES,
  NOTIFICATION_HEADER_STYLES,
  NotificationEventType,
  NotificationStatus,
  READ_MENTIONS,
  READ_MESSAGES,
  READ_REPLIES,
} from '~/lib/constants/notifications.constants';
import {
  READ,
  UNREAD,
  getNotificationCounts,
  getRelatedNotificationsByStatus,
  isOneOnOneNotification,
} from '~/lib/util/notification.util';

import NotificationCounts from './NotificationCounts';
import NotificationAvatar from '../NotificationAvatar';
import NotificationDetailWrapper from '../NotificationDetailWrapper';
import NotificationTimeStamp from '../NotificationTimeStamp';

const TOP_LEVEL_ID = 'notification-chat';

interface NotificationChatProps {
  channelName?: string;
  createdAt?: string;
  eventType?: NotificationEventType;
  groupBy: string;
  isRead?: boolean;
  notificationExternalId?: string;
  onUpdateNotification?: (
    notificationExternalIds: Array<string>,
    status: NotificationStatus,
  ) => void;
  patientName?: string;
  relatedNotifications: RelatedNotificationType[];
}

const { paragraph, paragraphBold, label } = EquipUITheme.typography;

const NotificationChat = ({
  channelName,
  createdAt,
  eventType,
  groupBy,
  isRead,
  notificationExternalId,
  onUpdateNotification,
  patientName,
  relatedNotifications,
}: NotificationChatProps) => {
  const {
    newMentionCounts,
    newMessageCounts,
    newReplyCounts,
    oldMentionCounts,
    oldMessageCounts,
    oldReplyCounts,
  } = useMemo<{
    newMentionCounts: number;
    newMessageCounts: number;
    newReplyCounts: number;
    oldMentionCounts: number;
    oldMessageCounts: number;
    oldReplyCounts: number;
  }>(() => {
    const readUnread = getRelatedNotificationsByStatus(relatedNotifications);

    const newCounts: Record<
      | NotificationEventType.CHAT_MENTION
      | NotificationEventType.CHAT_MESSAGE
      | NotificationEventType.CHAT_REPLY,
      number
    > = getNotificationCounts(isRead, eventType, readUnread[UNREAD]);

    const oldCounts: Record<
      | NotificationEventType.CHAT_MENTION
      | NotificationEventType.CHAT_MESSAGE
      | NotificationEventType.CHAT_REPLY,
      number
    > = getNotificationCounts(!isRead, eventType, readUnread[READ]);

    return {
      newMentionCounts: newCounts[NotificationEventType.CHAT_MENTION],
      newMessageCounts: newCounts[NotificationEventType.CHAT_MESSAGE],
      newReplyCounts: newCounts[NotificationEventType.CHAT_REPLY],
      oldMentionCounts: oldCounts[NotificationEventType.CHAT_MENTION],
      oldMessageCounts: oldCounts[NotificationEventType.CHAT_MESSAGE],
      oldReplyCounts: oldCounts[NotificationEventType.CHAT_REPLY],
    };
  }, [eventType, relatedNotifications]);
  const isOneOnOne = isOneOnOneNotification(groupBy);
  return (
    <>
      <ChakraFlex alignItems="start" justifyContent="space-between">
        <NotificationAvatar patientName={patientName} />
        <ChakraBox
          {...paragraph}
          id={`${TOP_LEVEL_ID}-header`}
          {...NOTIFICATION_HEADER_STYLES}
        >
          <span>You have {isRead ? 'read' : 'unread'} messages in</span>
          &nbsp;
          <span style={{ ...paragraphBold, lineHeight: '16.8px' }}>
            {patientName} - {channelName}
          </span>
        </ChakraBox>
        <NotificationTimeStamp
          createdAt={createdAt}
          isRead={isRead}
          notificationExternalId={notificationExternalId}
          onUpdateNotification={onUpdateNotification}
          relatedNotifications={relatedNotifications}
        />
      </ChakraFlex>
      <ChakraBox {...label} margin="8px 40px 0px">
        {!isRead && (
          <ChakraFlex marginBottom="4px">
            <NotificationDetailWrapper>
              <NotificationCounts
                count={isOneOnOne ? newMessageCounts : newReplyCounts}
                text={isOneOnOne ? NEW_MESSAGES : NEW_REPLIES}
              />
            </NotificationDetailWrapper>
            <NotificationDetailWrapper>
              <NotificationCounts
                count={newMentionCounts}
                text={NEW_MENTIONS}
              />
            </NotificationDetailWrapper>
          </ChakraFlex>
        )}
        <ChakraFlex>
          <NotificationDetailWrapper>
            <NotificationCounts
              count={isOneOnOne ? oldMessageCounts : oldReplyCounts}
              text={isOneOnOne ? READ_MESSAGES : READ_REPLIES}
            />
          </NotificationDetailWrapper>
          <NotificationDetailWrapper>
            <NotificationCounts count={oldMentionCounts} text={READ_MENTIONS} />
          </NotificationDetailWrapper>
        </ChakraFlex>
      </ChakraBox>
    </>
  );
};

export default NotificationChat;
